import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Refresh, Database, Activity, AlertTriangle } from "@/components/icons";

import { fetchCacheStats } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stat } from "@/components/Stat";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { formatDuration, formatNumber, formatPercent } from "@voltie/localization-web";
import { useT } from "@/app/localization";

const fmtInt = (n: number) => formatNumber(n, 0);

function backendBadgeClass(backend: string): string {
  switch (backend) {
    case "redis":
      return "bg-success/20 text-success";
    case "memory":
      return "bg-warning/20 text-warning";
    default:
      return "bg-error/20 text-error";
  }
}

export function CacheStatsPage() {
  const t = useT();
  const statsQ = useQuery({
    queryKey: ["cache", "stats"],
    queryFn: fetchCacheStats,
    refetchInterval: 5_000,
  });

  const data = statsQ.data;
  const total = data ? data.hits + data.misses : 0;
  const hitPct = data ? data.hit_rate * 100 : 0;
  const missPct = total ? (data!.misses / total) * 100 : 0;
  const badge = data
    ? { label: t("operator.ops.cache.backend", { backend: data.backend === "redis" || data.backend === "memory" ? data.backend : "other" }), cls: backendBadgeClass(data.backend) }
    : null;

  // A low hit rate under meaningful traffic is the signal worth flagging:
  // build_fn() (incl. the 23k-unit sweep behind /api/units) is running often.
  const lowHitRate = !!data && data.enabled && total > 200 && data.hit_rate < 0.9;

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.ops.cache.title")}</h1>
          <div className="text-sm opacity-70">
            {t("operator.ops.cache.subtitle")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${badge.cls}`}>
              {badge.label}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={() => statsQ.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
        </div>
      </div>

      {statsQ.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {t("operator.ops.cache.load_failed")}
        </div>
      ) : (
        <>
          {lowHitRate && (
            <div className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {t("operator.ops.cache.low_hit_rate")}
              </span>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Stat
              title={t("operator.ops.cache.hit_rate")}
              value={formatPercent(hitPct / 100, 1)}
              icon={<Activity className="h-4 w-4 opacity-60" />}
              description={t("operator.ops.cache.cacheable_requests", { count: total })}
            />
            <Stat title={t("operator.ops.cache.hits")} value={fmtInt(data?.hits ?? 0)} />
            <Stat title={t("operator.ops.cache.misses")} value={fmtInt(data?.misses ?? 0)} />
            <Stat
              title={t("operator.ops.cache.bypassed")}
              value={fmtInt(data?.bypassed ?? 0)}
              description={t("operator.ops.cache.bypassed_hint")}
            />
          </div>

          <Card>
            <CardContent className="space-y-4 py-5">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Database className="h-4 w-4" /> {t("operator.ops.cache.distribution")}
              </CardTitle>

              {total === 0 ? (
                <div className="text-sm opacity-60">{t("operator.ops.cache.empty")}</div>
              ) : (
                <>
                  <div className="flex h-6 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="flex items-center justify-center bg-success/70 text-[10px] font-medium text-success-content"
                      style={{ width: `${hitPct}%` }}
                      title={t("operator.ops.cache.hits_tooltip", { count: fmtInt(data!.hits), percent: formatPercent(hitPct / 100, 1) })}
                    >
                      {hitPct >= 12 ? formatPercent(hitPct / 100, 0) : ""}
                    </div>
                    <div
                      className="flex items-center justify-center bg-error/60 text-[10px] font-medium text-error-content"
                      style={{ width: `${missPct}%` }}
                      title={t("operator.ops.cache.misses_tooltip", { count: fmtInt(data!.misses), percent: formatPercent(missPct / 100, 1) })}
                    >
                      {missPct >= 12 ? formatPercent(missPct / 100, 0) : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs opacity-70">
                    <span className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded bg-success/70" /> {t("operator.ops.cache.hits")}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded bg-error/60" /> {t("operator.ops.cache.misses")}
                    </span>
                  </div>
                </>
              )}

              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 border-t border-border pt-4 text-sm sm:grid-cols-3">
                <div>
                  <dt className="opacity-60">{t("operator.ops.cache.enabled")}</dt>
                  <dd className="font-mono">{data ? String(data.enabled) : "—"}</dd>
                </div>
                <div>
                  <dt className="opacity-60">{t("operator.ops.cache.backend_label")}</dt>
                  <dd className="font-mono">{data?.backend ?? "—"}</dd>
                </div>
                <div>
                  <dt className="opacity-60">{t("operator.ops.auto_refresh")}</dt>
                  <dd className="font-mono">{formatDuration(5, "seconds")}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
